import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { setApiViewAs } from "../lib/api";
import { applyScenario, DEFAULT_SCENARIO, SCENARIOS, defaultPeriodMonths } from "../lib/scenario";
import { unionMonths } from "../lib/dateWindow";

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [scenario, setScenario] = useState(() => {
    // Sanitise persisted value — removed scenarios (e.g. old "weighted")
    // fall back to the default.
    const stored = localStorage.getItem("mm_scenario");
    return stored && SCENARIOS[stored] ? stored : DEFAULT_SCENARIO;
  });
  const [monthsRange, setMonthsRange] = useState(() => defaultPeriodMonths()); // rolling 12 from current month
  // Admin "view as principal" — persisted so the acting context survives a
  // page reload (backend ignores ?as= for non-admins, so a stale key is inert).
  const [viewAs, setViewAsState] = useState(() => localStorage.getItem("mm_view_as") || null);

  const setViewAs = (id) => {
    setViewAsState(id || null);
    if (id) localStorage.setItem("mm_view_as", id);
    else localStorage.removeItem("mm_view_as");
  };

  // Mirror viewAs into the api module so every request (reads AND writes)
  // carries ?as=<id> when view-as mode is active. Reads come back scoped to
  // that principal; portfolio writes apply on their behalf.
  useEffect(() => { setApiViewAs(viewAs); }, [viewAs]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = viewAs ? `/data?as=${encodeURIComponent(viewAs)}` : "/data";
      const res = await api.get(url);
      setRawData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [viewAs]);

  useEffect(() => { refresh(); }, [refresh]);

  // If the current period selector is outside all stored months (nothing to
  // show), fall back to the rolling default — but otherwise leave the user's
  // selection alone.
  useEffect(() => {
    if (!rawData?.meta?.months) return;
    setMonthsRange((curr) => {
      if (!curr || !curr.length) return defaultPeriodMonths();
      return curr;
    });
  }, [rawData]);

  // Union stored months with the current selection so any months the user
  // picks (past OR future) exist on the rawData window before applyScenario
  // narrows it. Prevents "no columns rendered" when the window extends
  // beyond stored data.
  const rawDataForRender = useMemo(() => {
    if (!rawData) return null;
    const stored = rawData.meta?.months || [];
    const merged = unionMonths(stored, monthsRange || []);
    if (merged.length === stored.length) return rawData;
    return { ...rawData, meta: { ...rawData.meta, months: merged } };
  }, [rawData, monthsRange]);

  const data = useMemo(
    () => (rawDataForRender ? applyScenario(rawDataForRender, scenario, monthsRange) : null),
    [rawDataForRender, scenario, monthsRange]
  );

  const changeScenario = (s) => {
    setScenario(s);
    localStorage.setItem("mm_scenario", s);
  };

  const replaceData = async (json) => {
    const res = await api.put("/data", json);
    setRawData(res.data);
    return res.data;
  };

  const updateNote = async (projectId, note) => {
    await api.put(`/data/projects/${projectId}/note`, { note });
    setRawData((d) => {
      if (!d) return d;
      return {
        ...d,
        projects: d.projects.map((p) =>
          p.id === projectId ? { ...p, note } : p
        ),
      };
    });
  };

  // Confirm invoicing for the current calendar month (caller's principal).
  const confirmInvoicing = async () => {
    const { data: res } = await api.post("/data/invoicing/confirm");
    setRawData((d) => {
      if (!d) return d;
      const inv = { ...(d.invoicing || {}) };
      inv[res.principalId] = { ...(inv[res.principalId] || {}), [res.monthKey]: res };
      return { ...d, invoicing: inv };
    });
    return res;
  };

  // Set a person's leave for one week (admin only). Optimistically merged
  // into rawData so Capacity reflects it without a refetch.
  const setLeave = async (personId, week, value) => {
    await api.put(`/leave/${encodeURIComponent(personId)}`, { week, value });
    setRawData((d) => {
      if (!d) return d;
      const leave = { ...(d.leave || {}) };
      const byWeek = { ...(leave[personId] || {}) };
      if (value === 0) delete byWeek[week];
      else byWeek[week] = value;
      leave[personId] = byWeek;
      return { ...d, leave };
    });
  };

  const updateReportNotes = async (notes) => {
    const { data: res } = await api.put("/data/notes", { notes });
    setRawData((d) => {
      if (!d) return d;
      return {
        ...d,
        meta: {
          ...d.meta,
          notes: res.notes,
          notesUpdatedAt: res.notesUpdatedAt,
          notesUpdatedBy: res.notesUpdatedBy,
        },
      };
    });
    return res;
  };

  return (
    <DataContext.Provider
      value={{
        rawData,
        data,
        loading,
        error,
        refresh,
        replaceData,
        updateNote,
        updateReportNotes,
        confirmInvoicing,
        setLeave,
        scenario,
        setScenario: changeScenario,
        monthsRange,
        setMonthsRange,
        viewAs,
        setViewAs,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
